/**
 * ComponentLibrary Component for UI-Model-1
 *
 * Left panel containing draggable components from Model Spec:
 * - Submodels with their fields, categories, and connectors
 * - Widgets (divider, button, contentBlock, yourBuild, build)
 *
 * Features:
 * - Multi-select with checkboxes
 * - Batch add to selected container
 * - Drag-and-drop for single items
 *
 * Data source: userProjectDataModels from ModuleContext
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { useModule } from '../../contexts/ModuleContext';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import {
  TSubmodel,
} from '../../../Pages/DefineModel/model.d';
import {
  SubModelIcon,
  CategoryIcon as GmsCategoryIcon,
  ConnectorIcon as GmsConnectorIcon,
  TextIcon,
  BooleanIcon,
  MultiChoiceIcon,
  IntegerIcon,
  DecimalIcon,
  FractionIcon,
  ButtonWidgetIcon,
  ContentBlockIcon,
  BuildIcon,
  DividerIcon,
} from 'assests/GmsIcons';
import Button from 'Components/DesignSystem/Button/Button';
import Search from 'Components/DesignSystem/Search/Search';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import {
  createFieldAPI,
  createWidgetAPI,
  createCategoryAPI,
  createConnectorAPI,
  buildDropDataConfiguration,
} from '../../types/api.types';
import { createAddItemCommand, createBatchCommand } from '../../types/command.types';
import { useContainerOperations } from '../../hooks/useContainerOperations';
import { canAddSubmodelItemToContainer, LockCheckResult } from '../../container-management/validators/submodelLockValidator';
import { generateId, generateInstanceId } from '../../utils/idGenerator';
import { isFreestyle } from '../../selectors';
import { ConfirmationDialog } from '../Common/ConfirmationDialog';
import { ItemAPI, FieldType, WidgetType, WidgetAPI } from '../../types/state.types';
import { createWidgetConfig } from '../../config/widgetDefaults';
import { createYourBuildConfig, mergeWithDefaults as mergeYourBuildWithDefaults } from '../../config/widgetDefaults/yourBuild.config';
import { createFieldConfig } from '../../config/fieldDefaults';
import { createCategoryConfig } from '../../config/fieldDefaults/category.config';
import { useSaveQueue } from '../../contexts/SaveQueueContext';

// ============================================================================
// Types
// ============================================================================

/**
 * Child item definition for adding submodel with all children
 */
export interface SubmodelChildItem {
  id: string;
  type: 'field' | 'category' | 'connector';
  name: string;
  fieldType?: string;
  parentId: string;
  categoryId?: string;
  categoryName?: string;
  categoryType?: string;
  dataConfiguration?: Record<string, unknown>;
}

interface ComponentLibraryProps {
  id?: string;
  selectedContainerId?: string | null;
  onAddItems?: (items: SelectedItem[]) => void;
}

interface SelectedItem {
  id: string;
  type: 'field' | 'category' | 'connector' | 'widget';
  name: string;
  fieldType?: string;
  widgetType?: string;
  parentId?: string;
  categoryId?: string;
  categoryName?: string;
  categoryType?: string;
  dataConfiguration?: Record<string, unknown>;
  submodelName?: string;
  /** Model spec ID (e.g. field.id, conn.id, cat.id) — used as the canvas item ID when present */
  specId?: string;
}

// ============================================================================
// Icon Helpers
// ============================================================================

/**
 * Get icon for field type
 */
const getFieldTypeIcon = (fieldType: string): React.ReactNode => {
  switch (fieldType?.toLowerCase()) {
    case 'text':
      return <TextIcon />;
    case 'integer':
      return <IntegerIcon />;
    case 'decimal':
      return <DecimalIcon />;
    case 'boolean':
      return <BooleanIcon />;
    case 'multichoice':
    case 'multi-choice':
      return <MultiChoiceIcon />;
    case 'fraction':
      return <FractionIcon />;
    default:
      return <TextIcon />;
  }
};

/**
 * Get icon for item type (category, connector, submodel)
 */
const getItemTypeIcon = (type: string): React.ReactNode => {
  switch (type) {
    case 'category':
      return <GmsCategoryIcon />;
    case 'connector':
      return <GmsConnectorIcon />;
    case 'submodel':
      return <SubModelIcon />;
    default:
      return null;
  }
};

/**
 * Get icon for widget type
 */
const getWidgetTypeIcon = (widgetType: string): React.ReactNode => {
  switch (widgetType) {
    case 'divider':
      return <DividerIcon />;
    case 'button':
      return <ButtonWidgetIcon />;
    case 'contentBlock':
      return <ContentBlockIcon />;
    case 'yourBuild':
      return <BuildIcon />;
    case 'build':
      return <BuildIcon />;
    default:
      return <ButtonWidgetIcon />;
  }
};

// ============================================================================
// Collapse Icon with Rotation
// ============================================================================

const CollapseIcon = ({ isExpanded }: { isExpanded: boolean }) => (
  <svg
    className={`tw-w-4 tw-h-4 tw-text-ink-secondary tw-transition-transform ${isExpanded ? 'tw-rotate-90' : ''}`}
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M6 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ============================================================================
// Drag Grip Icon (smaller version of DraggableItem's 6-dot pattern)
// ============================================================================

const DragGripIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="8"
    height="14"
    viewBox="0 0 12 16"
    fill="currentColor"
  >
    <circle cx="3" cy="3" r="1.5" />
    <circle cx="9" cy="3" r="1.5" />
    <circle cx="3" cy="8" r="1.5" />
    <circle cx="9" cy="8" r="1.5" />
    <circle cx="3" cy="13" r="1.5" />
    <circle cx="9" cy="13" r="1.5" />
  </svg>
);

// ============================================================================
// Multiselect Toggle Icon (checklist with two rows)
// ============================================================================

const MultiselectToggleIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Top checkbox */}
    <rect x="1" y="1" width="5" height="5" rx="1" />
    <path d="M2.5 3.5L3.5 4.5L5.5 2" />
    {/* Top line */}
    <line x1="8.5" y1="3.5" x2="15" y2="3.5" />
    {/* Bottom checkbox */}
    <rect x="1" y="10" width="5" height="5" rx="1" />
    <path d="M2.5 12.5L3.5 13.5L5.5 11" />
    {/* Bottom line */}
    <line x1="8.5" y1="12.5" x2="15" y2="12.5" />
  </svg>
);

// ============================================================================
// Selectable Item Component
// ============================================================================

interface SelectableItemProps {
  id: string;
  type: 'field' | 'category' | 'connector' | 'widget';
  name: string;
  fieldType?: string;
  widgetType?: string;
  parentId?: string;
  categoryId?: string;
  categoryName?: string;
  categoryType?: string;
  dataConfiguration?: Record<string, unknown>;
  submodelName?: string;
  /** Model spec ID (e.g. field.id, conn.id, cat.id) — used as the canvas item ID when present */
  specId?: string;
  isSelected: boolean;
  onToggle: (item: SelectedItem) => void;
  /** Whether the item is disabled (e.g., already used in a container) */
  isDisabled?: boolean;
  /** Reason for being disabled, shown in tooltip */
  disabledReason?: string;
  /** Whether multiselect mode is active (shows checkboxes) */
  isMultiselectMode: boolean;
}

const SelectableItem = ({
  id,
  type,
  name,
  fieldType,
  widgetType,
  parentId,
  categoryId,
  categoryName,
  categoryType,
  dataConfiguration,
  submodelName,
  specId,
  isSelected,
  onToggle,
  isDisabled = false,
  disabledReason,
  isMultiselectMode,
}: SelectableItemProps) => {
  const itemData: SelectedItem = {
    id,
    type,
    name,
    fieldType,
    widgetType,
    parentId,
    categoryId,
    categoryName,
    categoryType,
    dataConfiguration,
    submodelName,
    specId,
  };

  // Get the appropriate icon based on type
  const typeIcon = type === 'field'
    ? getFieldTypeIcon(fieldType || 'text')
    : type === 'widget'
      ? getWidgetTypeIcon(widgetType || 'button')
      : getItemTypeIcon(type);

  const handleDragStart = (e: React.DragEvent) => {
    // Prevent dragging if disabled
    if (isDisabled) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        type,
        id,
        name,
        fieldType,
        widgetType,
        parentId,
        categoryId,
        categoryName,
        categoryType,
        dataConfiguration,
        submodelName,
        specId,
        source: 'library', // Distinguish library drags from container item drags
      })
    );
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isDisabled) return;
    if (!isMultiselectMode) return;
    if ((e.target as HTMLElement).closest('[role="checkbox"]')) return;
    onToggle(itemData);
  };

  const handleCheckboxChange = () => {
    if (!isDisabled) onToggle(itemData);
  };

  return (
    <div role="button"
      id={id}
      data-testid={id}
      draggable={!isDisabled && !isMultiselectMode}
      onDragStart={handleDragStart}
      onClick={handleClick}
      title={isDisabled ? disabledReason : undefined}
      className={`tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1.5 tw-mb-1 tw-rounded tw-border tw-transition-colors ${isDisabled
          ? 'tw-bg-surface-sunken tw-border-edge tw-cursor-not-allowed'
          : isMultiselectMode
            ? isSelected
              ? 'tw-border-accent tw-bg-accent-tint tw-cursor-pointer'
              : 'tw-bg-surface tw-border-edge hover:tw-border-accent hover:tw-bg-surface-hover tw-cursor-pointer'
            : 'tw-bg-surface tw-border-edge hover:tw-border-accent hover:tw-bg-surface-hover tw-cursor-grab active:tw-cursor-grabbing'
        }`}
    >
      {/* Drag grip and checkbox are mutually exclusive */}
      {isMultiselectMode ? (
        <Checkbox
          checked={isSelected && !isDisabled}
          onChange={handleCheckboxChange}
          disabled={isDisabled}
          size="sm"
          className="tw-flex-shrink-0"
        />
      ) : (
        <DragGripIcon className={`tw-flex-shrink-0 ${isDisabled ? 'tw-text-ink-disabled' : 'tw-text-ink-tertiary'}`} />
      )}
      <span className={`tw-text-sm tw-truncate tw-flex-1 ${isDisabled ? 'tw-text-ink-disabled' : 'tw-text-ink'}`}>
        {name}
      </span>
      {/* Type icon */}
      {typeIcon && (
        <span className={`tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 [&_path]:tw-stroke-current icon-follow-ink ${isDisabled ? 'tw-text-ink-disabled' : 'tw-text-ink-secondary'}`}>
          {typeIcon}
        </span>
      )}
    </div>
  );
};

// ============================================================================
// Collapsible Section Component
// ============================================================================

const Section = ({ id, title, count, defaultExpanded = true, forceCollapsed, icon, children }: {
  id: string;
  title: string;
  count?: number;
  defaultExpanded?: boolean;
  forceCollapsed?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Respond to collapse all / expand all
  useEffect(() => {
    if (forceCollapsed !== undefined) {
      setIsExpanded(!forceCollapsed);
    }
  }, [forceCollapsed]);

  return (
    <div id={id} className="tw-border-b tw-border-edge">
      <button data-testid={`${id}-toggle`}
        onClick={() => setIsExpanded(!isExpanded)}
        className="tw-w-full tw-px-3 tw-py-2 tw-flex tw-justify-between tw-items-center tw-text-left tw-bg-surface-sunken hover:tw-bg-surface-hover"
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <CollapseIcon isExpanded={isExpanded} />
          {icon && (
            <span className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-text-ink-secondary [&_path]:tw-stroke-current icon-follow-ink">
              {icon}
            </span>
          )}
          <span className="tw-text-sm tw-font-semibold tw-text-ink">
            {title}
          </span>
        </div>
        {count !== undefined && (
          <span className="tw-text-xs tw-text-ink-secondary">{count}</span>
        )}
      </button>
      {isExpanded && <div className="tw-px-3 tw-py-2">{children}</div>}
    </div>
  );
};

// ============================================================================
// Submodel Section Component
// ============================================================================

interface SubmodelSectionProps {
  submodel: TSubmodel;
  searchTerm: string;
  baseId: string;
  selectedItems: Map<string, SelectedItem>;
  onToggleSelection: (item: SelectedItem) => void;
  forceCollapsed?: boolean;
  /** Check if a field is already used in ANY container (globally disabled). */
  isFieldUsed: (fieldName: string, parentId?: string) => boolean;
  /** Get usage info for a field */
  getFieldUsageInfo: (fieldName: string, parentId?: string) => { containerName: string; pageName?: string } | undefined;
  /** Check if a category is already used in ANY container (globally disabled). */
  isCategoryUsed: (categoryId: string, parentId?: string) => boolean;
  /** Get usage info for a category */
  getCategoryUsageInfo: (categoryId: string, parentId?: string) => { containerName: string; pageName?: string } | undefined;
  /** Check if a connector is already used in ANY container (globally disabled). */
  isConnectorUsed: (name: string, parentId?: string) => boolean;
  /** Get usage info for a connector */
  getConnectorUsageInfo: (name: string, parentId?: string) => { containerName: string; pageName?: string } | undefined;
  /** Currently selected container ID (used for submodel-level checks) */
  selectedContainerId: string | null;
  /** Check if submodel has fields used in OTHER containers */
  isSubmodelUsedElsewhere: (submodelId: string, currentContainerId: string | null) => boolean;
  /** Get container name where submodel fields are used */
  getSubmodelUsageContainer: (submodelId: string, currentContainerId: string | null) => string | null;
  /** Whether multiselect mode is active */
  isMultiselectMode: boolean;
  /** Rules 1/2/4/6: whether this submodel may be added to the selected container. */
  containerLockResult?: LockCheckResult;
  /** Rule 2: Returns page name if submodel is locked to a different page (or null) */
  getSubmodelPageLock: (submodelId: string, currentCanvasId: string | null) => string | null;
  /** The active canvas ID for the current page */
  activeCanvasId: string | null;
  /** Free style mode: no grey-out or lock-based disabling (duplicates confirmed on add) */
  freestyle: boolean;
}

const SubmodelSection = ({
  submodel,
  searchTerm,
  baseId,
  selectedItems,
  onToggleSelection,
  forceCollapsed,
  isFieldUsed,
  getFieldUsageInfo,
  isCategoryUsed,
  getCategoryUsageInfo,
  isConnectorUsed,
  getConnectorUsageInfo,
  selectedContainerId,
  isSubmodelUsedElsewhere,
  getSubmodelUsageContainer,
  isMultiselectMode,
  containerLockResult,
  getSubmodelPageLock,
  activeCanvasId,
  freestyle,
}: SubmodelSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Respond to collapse all / expand all
  useEffect(() => {
    if (forceCollapsed !== undefined) {
      setIsExpanded(!forceCollapsed);
    }
  }, [forceCollapsed]);
  const submodelId = submodel.id || submodel.data.name;

  const submodelName = submodel.data.label || submodel.data.name;

  // Filter items by search term
  const filteredFields = useMemo(
    () =>
      submodel.data.fields?.filter((f) =>
        f.name.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [],
    [submodel.data.fields, searchTerm]
  );

  const filteredCategories = useMemo(
    () =>
      submodel.data.productCategories?.filter((c) =>
        c.data.name.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [],
    [submodel.data.productCategories, searchTerm]
  );

  const filteredConnectors = useMemo(
    () =>
      submodel.data.connectors?.filter((c) =>
        c.data.name.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [],
    [submodel.data.connectors, searchTerm]
  );

  const totalItems =
    filteredFields.length + filteredCategories.length + filteredConnectors.length;

  // Availability across ALL child types (global uniqueness, Rule 5) — drives both the
  // grey-out and which children the submodel-header drag is allowed to carry.
  const availableFieldsCount = filteredFields.filter(
    (f) => !isFieldUsed(f.name, submodelId)
  ).length;
  const availableChildrenCount =
    availableFieldsCount +
    filteredCategories.filter(
      (c) => !isCategoryUsed(c.data.categories?.data?.[0]?.id || '', submodelId)
    ).length +
    filteredConnectors.filter((c) => !isConnectorUsed(c.data.name, submodelId)).length;
  // True when the submodel has children but every one is already placed elsewhere,
  // so the header has nothing left to add.
  const noAvailableChildren = totalItems > 0 && availableChildrenCount === 0;

  // Determine availability state
  const hasPartialAvailability = availableFieldsCount > 0 && availableFieldsCount < filteredFields.length;
  const isFullyAvailable = filteredFields.length > 0 && availableFieldsCount === filteredFields.length;

  // Check if this submodel has fields in OTHER containers (submodel-level disabling)
  const isDisabledForCurrentContainer = isSubmodelUsedElsewhere(submodelId, selectedContainerId);
  const usedInContainerName = isDisabledForCurrentContainer
    ? getSubmodelUsageContainer(submodelId, selectedContainerId)
    : null;

  // Rules 1/2/4/6: per-container lock + max>1 subtree cascade + max propagation (pure validator).
  const isBlockedByContainerSubmodel = containerLockResult ? !containerLockResult.allowed : false;

  // Page-lock: submodel is bound on a different page.
  const lockedToPageName = getSubmodelPageLock(submodelId, activeCanvasId);
  const isPageLocked = lockedToPageName !== null;

  // Combined: any rule triggers disable. noAvailableChildren keeps the header consistent
  // with its (already grey) fields and prevents dragging a fully-placed submodel.
  // Free style: no lock rules apply — only a fully-placed submodel header stays
  // disabled (its remaining-children drag would carry nothing).
  const isDisabled = freestyle
    ? noAvailableChildren
    : isDisabledForCurrentContainer || isBlockedByContainerSubmodel || isPageLocked || noAvailableChildren;
  const submodelDisabledReason = freestyle
    ? (noAvailableChildren ? 'All components added — click any to add again' : null)
    : isBlockedByContainerSubmodel
    ? (containerLockResult?.message || 'Locked to another submodel')
    : isPageLocked
      ? `Locked to ${lockedToPageName}`
      : isDisabledForCurrentContainer
        ? `Fields used in ${usedInContainerName}`
        : noAvailableChildren
          ? 'All components already added'
          : null;

  /**
   * Collect all children for submodel drag data
   */
  const collectAllChildren = useCallback((): SubmodelChildItem[] => {
    const children: SubmodelChildItem[] = [];

    // Add available fields only — skip those already placed elsewhere (global uniqueness, Rule 5).
    filteredFields.forEach((field) => {
      if (isFieldUsed(field.name, submodelId)) return;
      children.push({
        id: field.id || `field-${field.name}`,
        type: 'field',
        name: field.name,
        fieldType: field.type,
        parentId: submodelId || '',
        dataConfiguration: field.dataConfiguration as Record<string, unknown>,
      });
    });

    // Add available categories only
    filteredCategories.forEach((cat) => {
      if (isCategoryUsed(cat.data.categories?.data?.[0]?.id || '', submodelId)) return;
      children.push({
        id: cat.id || `category-${cat.data.name}`,
        type: 'category',
        name: cat.data.name,
        parentId: submodelId || '',
        categoryId: cat.data.categories?.data?.[0]?.id,
        categoryName: cat.data.categories?.data?.[0]?.name || '',
        categoryType: cat.data.categories?.type || '',
      });
    });

    // Add available connectors only
    filteredConnectors.forEach((conn) => {
      if (isConnectorUsed(conn.data.name, submodelId)) return;
      children.push({
        id: conn.id || `connector-${conn.data.name}`,
        type: 'connector',
        name: conn.data.name,
        parentId: submodelId || '',
      });
    });

    return children;
  }, [filteredFields, filteredCategories, filteredConnectors, submodelId, isFieldUsed, isCategoryUsed, isConnectorUsed]);

  /**
   * Handle drag start for submodel header
   * Sets drag data with type: 'submodel' and all children
   */
  const handleSubmodelDragStart = useCallback((e: React.DragEvent) => {
    // Prevent dragging if disabled by any rule
    if (isDisabled) {
      e.preventDefault();
      return;
    }

    const children = collectAllChildren();
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        type: 'submodel',  // Distinguishes from individual item drags
        id: submodelId,
        name: submodelName,
        children,  // Include ALL children in the drag data
        source: 'library', // Distinguish library drags from container drags
      })
    );
    e.dataTransfer.effectAllowed = 'copy';
  }, [collectAllChildren, submodelId, submodelName, isDisabled]);

  // Hide submodel section if no items match search
  if (searchTerm && totalItems === 0) return null;

  return (
    <div
      className={`tw-border-b tw-border-edge last:tw-border-b-0 ${
        // Free style: items stay individually addable (duplicates via confirm),
        // so never fade the section — only the header's drag state changes.
        freestyle
          ? ''
          : isFullyAvailable && !isDisabled
            ? ''
            : isDisabled
              ? ''
              : hasPartialAvailability
                ? ''
                : ''
        }`}
      title={submodelDisabledReason || undefined}
    >
      {/* Draggable submodel header */}
      <div
        draggable={!isDisabled}
        onDragStart={handleSubmodelDragStart}
        className={`tw-flex tw-items-center tw-bg-surface-sunken ${isDisabled
            ? 'tw-cursor-not-allowed'
            : 'hover:tw-bg-surface-hover tw-cursor-grab active:tw-cursor-grabbing'
          }`}
        title={
          submodelDisabledReason
            ? submodelDisabledReason
            : `Drag to add ${submodelName} with ${totalItems} component(s)`
        }
      >
        <button data-testid={`${baseId}-submodel-${submodelId}-toggle`}
          onClick={() => setIsExpanded(!isExpanded)}
          className="tw-flex-1 tw-flex tw-flex-col tw-px-3 tw-py-2 tw-text-left"
        >
          {/* First row: icon, name, count */}
          <div className="tw-flex tw-items-center tw-gap-2 tw-w-full">
            <CollapseIcon isExpanded={isExpanded} />
            {/* Submodel icon */}
            <span className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-text-ink-secondary [&_path]:tw-stroke-current icon-follow-ink">
              <SubModelIcon />
            </span>
            <span className={`tw-flex-1 tw-text-sm tw-font-semibold ${freestyle
                ? 'tw-text-ink'
                : isDisabled
                  ? 'tw-text-ink-disabled'
                  : isFullyAvailable
                    ? 'tw-text-ink'
                    : hasPartialAvailability
                      ? 'tw-text-ink-secondary'
                      : 'tw-text-ink'
              }`}>
              {submodelName}
            </span>
            <span className="tw-text-xs tw-text-ink-secondary">{totalItems}</span>
          </div>
          {/* Second row: disabled reason message (below name) */}
          {isDisabled && submodelDisabledReason && (
            <span className="tw-text-xs tw-text-ink-tertiary tw-pl-10 tw-mt-0.5">
              ({submodelDisabledReason})
            </span>
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="tw-px-3 tw-py-2 tw-pl-6">
          {/* Fields */}
          {filteredFields.length > 0 && (
            <div className="tw-mb-2">
              <div className="tw-text-xs tw-text-ink-secondary tw-uppercase tw-mb-1 tw-font-medium">
                Fields
              </div>
              {filteredFields.map((field, idx) => {
                const itemId = `${baseId}-submodel-${submodelId}-field-${field.id || idx}`;
                // Check if field is used in ANY container (globally disabled)
                const fieldIsUsed = isFieldUsed(field.name, submodelId);
                const usageInfo = fieldIsUsed ? getFieldUsageInfo(field.name, submodelId) : undefined;
                // Inherit submodel-level disabled state. Free style: always selectable
                // (re-adding an already-placed item asks for confirmation instead).
                const itemDisabled = freestyle ? false : fieldIsUsed || isDisabled;
                const itemReason = fieldIsUsed
                  ? (usageInfo ? `Already in ${usageInfo.pageName ? `${usageInfo.pageName}'s ${usageInfo.containerName}` : usageInfo.containerName}` : undefined)
                  : isDisabled ? (submodelDisabledReason || undefined) : undefined;
                return (
                  <SelectableItem
                    key={field.id || `field-${idx}`}
                    id={itemId}
                    type="field"
                    name={field.name}
                    fieldType={field.type}
                    parentId={submodelId || undefined}
                    dataConfiguration={
                      field.dataConfiguration as Record<string, unknown>
                    }
                    submodelName={submodelName}
                    specId={field.id || undefined}
                    isSelected={selectedItems.has(itemId)}
                    onToggle={onToggleSelection}
                    isDisabled={itemDisabled}
                    disabledReason={itemReason}
                    isMultiselectMode={isMultiselectMode}
                  />
                );
              })}
            </div>
          )}

          {/* Categories */}
          {filteredCategories.length > 0 && (
            <div className="tw-mb-2">
              <div className="tw-text-xs tw-text-ink-secondary tw-uppercase tw-mb-1 tw-font-medium">
                Categories
              </div>
              {filteredCategories.map((cat, idx) => {
                const itemId = `${baseId}-submodel-${submodelId}-category-${cat.id || idx}`;
                const categoryId = cat.data.categories?.data?.[0]?.id || '';
                // Check if category is used in ANY container (globally disabled)
                const catIsUsed = isCategoryUsed(categoryId, submodelId);
                const usageInfo = catIsUsed ? getCategoryUsageInfo(categoryId, submodelId) : undefined;
                // Inherit submodel-level disabled state. Free style: always selectable
                // (re-adding an already-placed item asks for confirmation instead).
                const itemDisabled = freestyle ? false : catIsUsed || isDisabled;
                const itemReason = catIsUsed
                  ? (usageInfo ? `Already in ${usageInfo.pageName ? `${usageInfo.pageName}'s ${usageInfo.containerName}` : usageInfo.containerName}` : undefined)
                  : isDisabled ? (submodelDisabledReason || undefined) : undefined;
                return (
                  <SelectableItem
                    key={cat.id || `category-${idx}`}
                    id={itemId}
                    type="category"
                    name={cat.data.name}
                    parentId={submodelId || undefined}
                    categoryId={categoryId}
                    categoryName={cat.data.categories?.data?.[0]?.name || ''}
                    categoryType={cat.data.categories?.type || ''}
                    submodelName={submodelName}
                    specId={cat.id || undefined}
                    isSelected={selectedItems.has(itemId)}
                    onToggle={onToggleSelection}
                    isDisabled={itemDisabled}
                    disabledReason={itemReason}
                    isMultiselectMode={isMultiselectMode}
                  />
                );
              })}
            </div>
          )}

          {/* Connectors */}
          {filteredConnectors.length > 0 && (
            <div className="tw-mb-2">
              <div className="tw-text-xs tw-text-ink-secondary tw-uppercase tw-mb-1 tw-font-medium">
                Connectors
              </div>
              {filteredConnectors.map((conn, idx) => {
                const itemId = `${baseId}-submodel-${submodelId}-connector-${conn.id || idx}`;
                // Check if connector is used in ANY container (globally disabled)
                const connIsUsed = isConnectorUsed(conn.data.name, submodelId);
                const usageInfo = connIsUsed ? getConnectorUsageInfo(conn.data.name, submodelId) : undefined;
                // Inherit submodel-level disabled state. Free style: always selectable
                // (re-adding an already-placed item asks for confirmation instead).
                const itemDisabled = freestyle ? false : connIsUsed || isDisabled;
                const itemReason = connIsUsed
                  ? (usageInfo ? `Already in ${usageInfo.pageName ? `${usageInfo.pageName}'s ${usageInfo.containerName}` : usageInfo.containerName}` : undefined)
                  : isDisabled ? (submodelDisabledReason || undefined) : undefined;
                return (
                  <SelectableItem
                    key={conn.id || `connector-${idx}`}
                    id={itemId}
                    type="connector"
                    name={conn.data.name}
                    parentId={submodelId || undefined}
                    submodelName={submodelName}
                    specId={conn.id || undefined}
                    isSelected={selectedItems.has(itemId)}
                    onToggle={onToggleSelection}
                    isDisabled={itemDisabled}
                    disabledReason={itemReason}
                    isMultiselectMode={isMultiselectMode}
                  />
                );
              })}
            </div>
          )}

          {/* Empty state for submodel */}
          {totalItems === 0 && !searchTerm && (
            <p className="tw-text-xs tw-text-ink-tertiary tw-italic">
              No components in this submodel
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const ComponentLibrary = ({
  id,
}: ComponentLibraryProps) => {
  const baseId = id || 'component-library';
  const { userProjectDataModels, getSubmodelInstanceLimits } = useModule();
  const {
    state,
    dispatch,
    getContainer,
    isFieldUsed,
    getFieldUsageInfo,
    isCategoryUsed,
    getCategoryUsageInfo,
    isConnectorUsed,
    getConnectorUsageInfo,
    isSubmodelUsedElsewhere,
    getSubmodelUsageContainer,
    getSubmodelPageLock,
  } = useUIModelState();
  const { addSubmodelToContainer } = useContainerOperations();
  const { enqueueSave } = useSaveQueue();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedItem>>(new Map());
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [isMultiselectMode, setIsMultiselectMode] = useState(false);

  const toggleCollapseAll = useCallback(() => {
    setAllCollapsed(prev => !prev);
  }, []);

  const toggleMultiselectMode = useCallback(() => {
    setIsMultiselectMode(prev => {
      if (prev) setSelectedItems(new Map());
      return !prev;
    });
  }, []);

  // Get selectedContainerId and activePageId from context
  const { selectedContainerId, activePageId } = state.editorState;

  // Free style: all submodel restrictions lifted; duplicates confirmed on add.
  const freestyle = isFreestyle(state.model);

  // Free style: a batch add containing already-placed items waits here for the
  // user's "add again?" confirmation.
  const [pendingDuplicateAdd, setPendingDuplicateAdd] = useState<{
    items: SelectedItem[];
    duplicates: SelectedItem[];
  } | null>(null);

  // Compute the active canvasId for Rule 2 page-locking
  const activeCanvasId = useMemo(() => {
    if (!state.model?.layout.canvas) return null;
    return Object.keys(state.model.layout.canvas).find(
      (key) => state.model!.layout.canvas[key].pageId === activePageId
    ) ?? null;
  }, [activePageId, state.model]);

  // Active canvas's top-level containers — the tree the lock validator runs within.
  const activeCanvasContainers = useMemo(
    () => (activeCanvasId && state.model?.layout.canvas[activeCanvasId]?.containers) || [],
    [activeCanvasId, state.model]
  );

  // Per-submodel lock result for the selected container, covering Rules 1/2/4/6
  // (per-container lock, no cascade for max=1, max>1 subtree cascade, max propagation).
  const submodelLockFor = useCallback(
    (smId: string): LockCheckResult => {
      // Free style: the per-container lock rules don't apply — everything is allowed.
      if (freestyle || !selectedContainerId) return { allowed: true };
      return canAddSubmodelItemToContainer(
        activeCanvasContainers,
        selectedContainerId,
        smId,
        (id) => getSubmodelInstanceLimits(id).maxInstantiation
      );
    },
    [freestyle, selectedContainerId, activeCanvasContainers, getSubmodelInstanceLimits]
  );

  const project = userProjectDataModels?.project;
  const submodels = project?.dataModels || [];
  const rootFields = project?.fields || [];
  const rootCategories = project?.productCategories || [];
  const rootConnectors = project?.connectors || [];

  // ============================================================================
  // Selection Handlers
  // ============================================================================

  const toggleSelection = useCallback((item: SelectedItem) => {
    setSelectedItems(prev => {
      // Deselecting — always allowed
      if (prev.has(item.id)) {
        const next = new Map(prev);
        next.delete(item.id);
        return next;
      }

      // Selecting — enforce single-submodel scope (free style allows mixing,
      // so the selection may span submodels there).
      // Treat parentId as the group key (undefined for root items).
      const existingItem = prev.size > 0 ? prev.values().next().value : null;
      const isSameGroup = freestyle || !existingItem || existingItem.parentId === item.parentId;

      const next = isSameGroup ? new Map(prev) : new Map<string, SelectedItem>();
      next.set(item.id, item);
      return next;
    });
  }, [freestyle]);

  const clearSelection = useCallback(() => {
    setSelectedItems(new Map());
  }, []);

  /**
   * Create an ItemAPI from a SelectedItem
   */
  const createItemFromSelection = useCallback((selection: SelectedItem, asDuplicate = false): ItemAPI => {
    // Use the model spec ID when present; a free-style duplicate gets an
    // instance-suffixed ID so both copies stay uniquely addressable.
    const itemId = asDuplicate && selection.specId
      ? generateInstanceId(selection.specId)
      : selection.specId || generateId(selection.type);

    switch (selection.type) {
      case 'field': {
        const fieldTypeStr = (selection.fieldType as FieldType) || 'text';
        const filteredDataConfig = buildDropDataConfiguration(fieldTypeStr, selection.dataConfiguration);
        const defaultConfig = createFieldConfig(fieldTypeStr, selection.name);
        return createFieldAPI(
          itemId,
          selection.name,
          fieldTypeStr,
          selection.parentId,
          {
            dataConfiguration: filteredDataConfig,
            configuration: defaultConfig,
          }
        );
      }

      case 'widget': {
        const wType = (selection.widgetType as WidgetType) || 'divider';

        if (wType === 'yourBuild') {
          // yourBuild uses configRef indirection — config lives in sharedWidgets
          const ybId = `your-build-widget-instance-${crypto.randomUUID()}`;
          // Preserve any config the user already set (e.g. via the header preview
          // panel or a prior instance) instead of clobbering it with fresh defaults.
          const existingConfig = state.model?.layout?.sharedWidgets?.yourBuildWidget;
          const resolvedConfig = existingConfig
            ? mergeYourBuildWithDefaults(existingConfig)
            : createYourBuildConfig();

          // Store config at model.layout.sharedWidgets.yourBuildWidget
          if (state.model) {
            dispatch({
              type: 'SET_MODEL',
              payload: {
                ...state.model,
                layout: {
                  ...state.model.layout,
                  sharedWidgets: {
                    ...state.model.layout?.sharedWidgets,
                    yourBuildWidget: resolvedConfig,
                  },
                },
              },
            });
          }

          enqueueSave('sharedWidgets', {
            widgetsData: { yourBuildWidget: resolvedConfig },
          });

          return createWidgetAPI(
            ybId,
            wType,
            selection.parentId,
            {
              name: selection.name || 'Your Build',
              configRef: 'yourBuildWidget',
            },
          );
        }

        const defaultConfig = createWidgetConfig(wType);
        const widgetOverrides: Partial<WidgetAPI> = {
          name: selection.name || wType,
          configuration: defaultConfig,
        };
        if (wType === 'contentBlock') {
          widgetOverrides.content = '';
        }
        return createWidgetAPI(
          itemId,
          wType,
          selection.parentId,
          widgetOverrides,
        );
      }

      case 'category': {
        const catDisplayConfig = createCategoryConfig(selection.name);
        return createCategoryAPI(
          itemId,
          selection.name,
          selection.categoryId || '',
          selection.parentId,
          {
            configuration: {
              ...catDisplayConfig,
              categoryId: selection.categoryId || '',
              categoryName: selection.categoryName || selection.name,
              categoryType: selection.categoryType || '',
              fieldName: selection.name,
              fieldType: 'category',
              displayName: selection.name,
              wrapperName: selection.name,
              id: itemId,
              titleText: `Select ${selection.name}`,
            },
          }
        );
      }

      case 'connector':
        return createConnectorAPI(
          itemId,
          selection.name,
          selection.parentId
        );

      default:
        // Default to field
        return createFieldAPI(itemId, selection.name, 'text', selection.parentId);
    }
  }, [state.model, dispatch, enqueueSave]);

  /**
   * Commit a batch add of items to the selected container. Items whose ID is in
   * duplicateIds are already placed elsewhere (free style) and get instance IDs.
   */
  const commitAddSelected = useCallback((items: SelectedItem[], duplicateIds: Set<string>) => {
    if (!selectedContainerId) {
      toast.error('Please select a container on the canvas first');
      return;
    }

    // Look up the actual canvas UUID key (activePageId !== canvasId)
    const canvasId = Object.keys(state.model?.layout.canvas || {}).find(
      (key) => state.model?.layout.canvas[key].pageId === activePageId
    );
    if (!canvasId) {
      toast.error('No active page found');
      return;
    }

    // Create a batch command for all items
    const addCommands = items.map((item) => {
      const newItem = createItemFromSelection(item, duplicateIds.has(item.id));
      return createAddItemCommand(
        {
          item: newItem,
          canvasId,
          containerId: selectedContainerId,
        },
        `Add ${item.type}: ${item.name}`
      );
    });

    // Execute as a batch command
    const batchCommand = createBatchCommand(
      addCommands,
      `Add ${items.length} component(s) to container`
    );

    dispatch({ type: 'EXECUTE_COMMAND', payload: batchCommand });

    // Set content.submodel for items that came from a submodel
    const submodelItems = items.filter(item => item.parentId && item.submodelName);
    if (submodelItems.length > 0) {
      // All items from a submodel share the same parentId (one submodel per container rule)
      const { parentId: submodelId, submodelName } = submodelItems[0];
      // Rule 1/2: bind the submodel to the container that HOLDS the field (its own),
      // whether root or nested — never the parent. Containers lock independently.
      addSubmodelToContainer(selectedContainerId, submodelId!, submodelName!);
    }

    // Clear selection
    clearSelection();

    toast.success(`Added ${items.length} component(s)`);
  }, [selectedContainerId, activePageId, state.model, createItemFromSelection, dispatch, clearSelection, getContainer, addSubmodelToContainer]);

  /**
   * Handle batch add of selected items to container
   */
  const handleAddSelected = useCallback(() => {
    if (!selectedContainerId) {
      toast.error('Please select a container on the canvas first');
      return;
    }

    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }

    const items = Array.from(selectedItems.values());

    // Free style: already-placed selections need the user's "add again?" nod first.
    if (freestyle) {
      const duplicates = items.filter((item) =>
        item.type === 'field'
          ? isFieldUsed(item.name, item.parentId)
          : item.type === 'category'
            ? isCategoryUsed(item.categoryId || '', item.parentId)
            : item.type === 'connector'
              ? isConnectorUsed(item.name, item.parentId)
              : false
      );
      if (duplicates.length > 0) {
        setPendingDuplicateAdd({ items, duplicates });
        return;
      }
    }

    commitAddSelected(items, new Set());
  }, [selectedContainerId, selectedItems, freestyle, isFieldUsed, isCategoryUsed, isConnectorUsed, commitAddSelected]);

  // ============================================================================
  // Filtered Data
  // ============================================================================

  // Filter root-level items by search
  const filteredRootFields = useMemo(
    () =>
      rootFields.filter((f) =>
        f.data.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [rootFields, searchTerm]
  );

  const filteredRootCategories = useMemo(
    () =>
      rootCategories.filter((c) =>
        c.data.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [rootCategories, searchTerm]
  );

  const filteredRootConnectors = useMemo(
    () =>
      rootConnectors.filter((c) =>
        c.data.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [rootConnectors, searchTerm]
  );

  // Widgets are always available
  const widgets = [
    { id: 'divider', name: 'Divider', widgetType: 'divider', icon: '━' },
    { id: 'contentBlock', name: 'Content Block', widgetType: 'contentBlock', icon: '☰' },
    { id: 'yourBuild', name: 'Your Build', widgetType: 'yourBuild', icon: '⚙' },
    { id: 'button', name: 'Button', widgetType: 'button', icon: '▢' },
    { id: 'build', name: 'Build Summary', widgetType: 'build', icon: '📋' },
  ];

  const filteredWidgets = useMemo(
    () =>
      widgets.filter((w) =>
        w.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [searchTerm]
  );

  const hasModelData =
    submodels.length > 0 ||
    rootFields.length > 0 ||
    rootCategories.length > 0 ||
    rootConnectors.length > 0;

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div
      id={baseId}
      data-testid={baseId}
      className="tw-w-64 tw-h-full tw-bg-surface tw-border-r tw-border-edge tw-overflow-y-auto tw-flex-shrink-0 tw-flex tw-flex-col"
    >
      {/* Search */}
      <div className="tw-p-3 tw-border-b tw-border-edge tw-flex-shrink-0">
        {/* DS Search (§ 87): names itself, clear affordance, same per-keystroke
            setSearchTerm — filteredFields recompute exactly as before. */}
        <Search
          size="sm"
          placeholder="Search components..."
          value={searchTerm}
          onChange={(value: string) => setSearchTerm(value)}
          data-testid="componentlibrary-componentlibrary-input-3"
        />
      </div>

      {/* Multiselect Toggle + Collapse All / Expand All Control */}
      <div className="tw-flex tw-justify-between tw-items-center tw-px-3 tw-py-1 tw-border-b tw-border-edge tw-flex-shrink-0">
        <button data-testid="componentlibrary-componentlibrary-button-4"
          id={`${baseId}-multiselect-toggle-btn`}
          onClick={toggleMultiselectMode}
          title={isMultiselectMode ? 'Exit multiselect mode' : 'Enter multiselect mode'}
          className={`tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded tw-transition-colors ${isMultiselectMode
              ? 'tw-bg-accent-tint tw-text-accent'
              : 'tw-text-ink-secondary hover:tw-bg-surface-hover'
            }`}
        >
          <MultiselectToggleIcon />
        </button>
        <button data-testid="componentlibrary-componentlibrary-button-5"
          id={`${baseId}-toggle-all-btn`}
          className="tw-text-xs tw-font-semibold tw-text-accent hover:tw-underline"
          onClick={toggleCollapseAll}
        >
          {allCollapsed ? 'Expand All' : 'Collapse All'}
        </button>
      </div>

      {/* Selection Actions - shown when multiselect is active and items selected */}
      {isMultiselectMode && selectedItems.size > 0 && (
        <div className="tw-px-2 tw-py-1.5 tw-bg-accent-tint tw-border-b tw-border-edge tw-flex tw-items-center tw-justify-between tw-flex-shrink-0">
          <span className="tw-text-xs tw-text-accent tw-font-medium">
            {selectedItems.size} selected
          </span>
          <div className="tw-flex tw-gap-2">
            <Button variant="tertiary" onClick={clearSelection} className="!tw-h-6 !tw-px-2 !tw-py-0 !tw-text-xs">
              Clear
            </Button>
            <Button
              variant="primary"
              onClick={handleAddSelected}
              disabled={!selectedContainerId}
              className="!tw-h-6 !tw-px-2 !tw-py-0 !tw-text-xs"
            >
              Add to Container
            </Button>
          </div>
        </div>
      )}

      {/* Scrollable content area */}
      <div className="tw-flex-1 tw-overflow-y-auto">
        {/* Submodels Section */}
        {submodels.length > 0 && (
          <div className="tw-border-b tw-border-edge">
            <div className="tw-px-3 tw-py-2 tw-bg-surface-sunken">
              <span className="tw-text-xs tw-font-semibold tw-text-ink-secondary tw-uppercase">
                Submodels
              </span>
            </div>
            {submodels.map((submodel, idx) => (
              <SubmodelSection
                key={submodel.id || idx}
                submodel={submodel}
                searchTerm={searchTerm}
                baseId={baseId}
                selectedItems={selectedItems}
                onToggleSelection={toggleSelection}
                forceCollapsed={allCollapsed}
                isFieldUsed={isFieldUsed}
                getFieldUsageInfo={getFieldUsageInfo}
                isCategoryUsed={isCategoryUsed}
                getCategoryUsageInfo={getCategoryUsageInfo}
                isConnectorUsed={isConnectorUsed}
                getConnectorUsageInfo={getConnectorUsageInfo}
                selectedContainerId={selectedContainerId}
                isSubmodelUsedElsewhere={isSubmodelUsedElsewhere}
                getSubmodelUsageContainer={getSubmodelUsageContainer}
                isMultiselectMode={isMultiselectMode}
                containerLockResult={submodelLockFor(submodel.id || submodel.data.name)}
                getSubmodelPageLock={getSubmodelPageLock}
                activeCanvasId={activeCanvasId}
                freestyle={freestyle}
              />
            ))}
          </div>
        )}

        {/* Root Fields Section */}
        {filteredRootFields.length > 0 && (
          <Section
            id={`${baseId}-root-fields`}
            title="Fields"
            count={filteredRootFields.length}
            defaultExpanded={true}
            forceCollapsed={allCollapsed}
          >
            {filteredRootFields.map((field, idx) => {
              const itemId = `${baseId}-root-field-${field.id || idx}`;
              // Check if field is used in ANY container (globally disabled)
              const fieldIsUsed = isFieldUsed(field.data.name);
              const usageInfo = fieldIsUsed ? getFieldUsageInfo(field.data.name) : undefined;
              // Format: "Already in Page X's Container Y" or "Already in Container Y" if no page
              const disabledReason = usageInfo
                ? `Already in ${usageInfo.pageName ? `${usageInfo.pageName}'s ${usageInfo.containerName}` : usageInfo.containerName}`
                : undefined;
              return (
                <SelectableItem
                  key={field.id || idx}
                  id={itemId}
                  type="field"
                  name={field.data.name}
                  fieldType={field.data.type}
                  dataConfiguration={
                    field.data.dataConfiguration as Record<string, unknown>
                  }
                  specId={field.id || undefined}
                  isSelected={selectedItems.has(itemId)}
                  onToggle={toggleSelection}
                  isDisabled={freestyle ? false : fieldIsUsed}
                  disabledReason={disabledReason}
                  isMultiselectMode={isMultiselectMode}
                />
              );
            })}
          </Section>
        )}

        {/* Root Categories Section */}
        {filteredRootCategories.length > 0 && (
          <Section
            id={`${baseId}-root-categories`}
            title="Categories"
            count={filteredRootCategories.length}
            defaultExpanded={true}
            forceCollapsed={allCollapsed}
            icon={<GmsCategoryIcon />}
          >
            {filteredRootCategories.map((cat, idx) => {
              const itemId = `${baseId}-root-category-${cat.id || idx}`;
              const categoryId = cat.data.categories?.data?.[0]?.id || '';
              // Check if category is used in ANY container (globally disabled)
              const catIsUsed = isCategoryUsed(categoryId);
              const usageInfo = catIsUsed ? getCategoryUsageInfo(categoryId) : undefined;
              const disabledReason = usageInfo
                ? `Already in ${usageInfo.pageName ? `${usageInfo.pageName}'s ${usageInfo.containerName}` : usageInfo.containerName}`
                : undefined;
              return (
                <SelectableItem
                  key={cat.id || idx}
                  id={itemId}
                  type="category"
                  name={cat.data.name}
                  categoryId={categoryId}
                  categoryName={cat.data.categories?.data?.[0]?.name || ''}
                  categoryType={cat.data.categories?.type || ''}
                  specId={cat.id || undefined}
                  isSelected={selectedItems.has(itemId)}
                  onToggle={toggleSelection}
                  isDisabled={freestyle ? false : catIsUsed}
                  disabledReason={disabledReason}
                  isMultiselectMode={isMultiselectMode}
                />
              );
            })}
          </Section>
        )}

        {/* Root Connectors Section */}
        {filteredRootConnectors.length > 0 && (
          <Section
            id={`${baseId}-root-connectors`}
            title="Connectors"
            count={filteredRootConnectors.length}
            defaultExpanded={true}
            forceCollapsed={allCollapsed}
            icon={<GmsConnectorIcon />}
          >
            {filteredRootConnectors.map((conn, idx) => {
              const itemId = `${baseId}-root-connector-${conn.id || idx}`;
              // Check if connector is used in ANY container (globally disabled)
              const connIsUsed = isConnectorUsed(conn.data.name);
              const usageInfo = connIsUsed ? getConnectorUsageInfo(conn.data.name) : undefined;
              const disabledReason = usageInfo
                ? `Already in ${usageInfo.pageName ? `${usageInfo.pageName}'s ${usageInfo.containerName}` : usageInfo.containerName}`
                : undefined;
              return (
                <SelectableItem
                  key={conn.id || idx}
                  id={itemId}
                  type="connector"
                  name={conn.data.name}
                  specId={conn.id || undefined}
                  isSelected={selectedItems.has(itemId)}
                  onToggle={toggleSelection}
                  isDisabled={freestyle ? false : connIsUsed}
                  disabledReason={disabledReason}
                  isMultiselectMode={isMultiselectMode}
                />
              );
            })}
          </Section>
        )}

        {/* Widgets Section */}
        {filteredWidgets.length > 0 && (
          <div className="tw-border-b tw-border-edge">
            <div className="tw-px-3 tw-py-2 tw-bg-surface-sunken">
              <span className="tw-text-xs tw-font-semibold tw-text-ink-secondary tw-uppercase">
                Widgets
              </span>
            </div>
            <div className="tw-px-3 tw-py-2">
              {filteredWidgets.map((widget) => {
                const itemId = `${baseId}-widget-${widget.id}`;
                return (
                  <SelectableItem
                    key={widget.id}
                    id={itemId}
                    type="widget"
                    name={widget.name}
                    widgetType={widget.widgetType}
                    isSelected={false}
                    onToggle={toggleSelection}
                    isMultiselectMode={false}
                    isDisabled={widget.id === 'build' || widget.id === 'button'}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!hasModelData && (
          <div className="tw-p-4 tw-text-center tw-text-ink-secondary tw-text-sm">
            <p>No model spec data available.</p>
            <p className="tw-text-xs tw-mt-1">
              Create submodels in the Model tab first.
            </p>
          </div>
        )}
      </div>

      {/* Free style: confirm before re-adding components that are already placed */}
      {pendingDuplicateAdd && (
        <ConfirmationDialog
          id={`${baseId}-duplicate-confirm`}
          isOpen
          title="Component already added"
          message={
            pendingDuplicateAdd.duplicates.length === 1
              ? `'${pendingDuplicateAdd.duplicates[0].name}' is already added to this model.`
              : `${pendingDuplicateAdd.duplicates.length} of the selected components are already added: ${pendingDuplicateAdd.duplicates.map((d) => d.name).join(', ')}.`
          }
          secondaryMessage="Would you like to add them again?"
          confirmText="Add Again"
          cancelText="Cancel"
          onConfirm={() => {
            commitAddSelected(
              pendingDuplicateAdd.items,
              new Set(pendingDuplicateAdd.duplicates.map((d) => d.id))
            );
            setPendingDuplicateAdd(null);
          }}
          onCancel={() => setPendingDuplicateAdd(null)}
        />
      )}
    </div>
  );
};

export default ComponentLibrary;
